import { useState, useEffect, useCallback } from 'react';
import { serviceRequestsService } from '../services/serviceRequests';
import type {
  ServiceRequestStats,
  ServiceRequestItem,
  ServiceRequestListParams,
} from '../types/serviceRequests';

// ============================================================================
// useServiceRequestStats
// ============================================================================

interface UseServiceRequestStatsReturn {
  data: ServiceRequestStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useServiceRequestStats(): UseServiceRequestStatsReturn {
  const [state, setState] = useState<{
    data: ServiceRequestStats | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await serviceRequestsService.getStats();
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch service request stats';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useServiceRequests — paginated list
// ============================================================================

interface UseServiceRequestsReturn {
  items: ServiceRequestItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useServiceRequests(params: ServiceRequestListParams): UseServiceRequestsReturn {
  const take = params.take || 20;
  const skip = params.skip || 0;

  const [state, setState] = useState<{
    items: ServiceRequestItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await serviceRequestsService.getList(params);
      setState({
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch service requests';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.status, params.skip, params.take]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(skip / take) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / take));

  return { ...state, page, totalPages, refetch: fetch };
}

// ============================================================================
// useServiceRequestPendingCount — lightweight hook for sidebar badge
// ============================================================================

interface UseServiceRequestPendingCountReturn {
  count: number;
  loading: boolean;
}

export function useServiceRequestPendingCount(): UseServiceRequestPendingCountReturn {
  const [state, setState] = useState<{ count: number; loading: boolean }>({
    count: 0,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    serviceRequestsService.getPendingCount()
      .then((data) => {
        if (!cancelled) setState({ count: data.count, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ count: 0, loading: false });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
}
